/**
 * BarChartEditor
 *
 * Property editor for bar chart configuration
 * Handles: datasource, series, axes, stacking, legend
 */

import React, { useCallback } from 'react';
import { ChartBaseEditor, type ChartBaseConfig } from './ChartBaseEditor';
import { AxisConfigurator } from './AxisConfigurator';
import { SelectPropertyEditor, type SelectOption } from '../../BaseEditors/SelectPropertyEditor';

export interface BarChartEditorProps {
  config: Partial<ChartBaseConfig & {
    stacking?: 'none' | 'positive' | 'percent';
    layout?: 'vertical' | 'horizontal';
    barCategoryGap?: number;
  }>;
  onChange: (config: Partial<ChartBaseConfig>) => void;
}

const seriesTypeOptions: SelectOption[] = [
  { value: 'bar', label: 'Bar' },
  { value: 'area', label: 'Area' },
  { value: 'line', label: 'Line' },
];

export const BarChartEditor: React.FC<BarChartEditorProps> = ({ config, onChange }) => {
  const handleAxesChange = useCallback(
    (axes: any) => {
      onChange({ ...config, axes });
    },
    [config, onChange],
  );

  return (
    <ChartBaseEditor
      config={config}
      onChange={onChange}
      chartType="Bar Chart"
      showAxes
      showSeriesType
      seriesTypeOptions={seriesTypeOptions}
      axesConfigurator={
        <AxisConfigurator
          axes={config.axes}
          onChange={handleAxesChange}
        />
      }
    >
      <div className="chart-editor__section">
        <h3 className="chart-editor__section-title">Bar Chart Options</h3>

        <SelectPropertyEditor
          label="Stacking"
          value={config.stacking || 'none'}
          onChange={(stacking) => onChange({ ...config, stacking })}
          options={[
            { value: 'none', label: 'None (Grouped)' },
            { value: 'positive', label: 'Stacked' },
            { value: 'percent', label: 'Percent Stack' },
          ]}
          helpText="How to combine multiple series"
        />

        <SelectPropertyEditor
          label="Layout"
          value={config.layout || 'vertical'}
          onChange={(layout) => onChange({ ...config, layout })}
          options={[
            { value: 'vertical', label: 'Vertical Bars' },
            { value: 'horizontal', label: 'Horizontal Bars' },
          ]}
          helpText="Bar orientation"
        />

        <div className="chart-editor__number-input">
          <label className="af-property-editor__label">Bar Gap (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="5"
            value={(config.barCategoryGap as number) || 10}
            onChange={(e) => onChange({ ...config, barCategoryGap: Number(e.target.value) })}
            className="af-property-editor__input"
          />
        </div>
      </div>
    </ChartBaseEditor>
  );
};

BarChartEditor.displayName = 'BarChartEditor';
